import React, { useState, useEffect, useContext, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { useRoleContext } from "../../context/RolesContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import logoutUser from "../../api/logout";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import MarkunreadMailboxOutlinedIcon from "@mui/icons-material/MarkunreadMailboxOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutline";
import RequestPageOutlinedIcon from "@mui/icons-material/RequestPageOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import FaxOutlinedIcon from "@mui/icons-material/FaxOutlined";
import DocumentScannerOutlinedIcon from "@mui/icons-material/DocumentScannerOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import ApiOutlinedIcon from "@mui/icons-material/ApiOutlined";
import IntegrationInstructionsOutlinedIcon from "@mui/icons-material/IntegrationInstructionsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import ManageHistoryOutlinedIcon from "@mui/icons-material/ManageHistoryOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import KeyboardDoubleArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftOutlined";
import KeyboardDoubleArrowRightOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowRightOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SourceOutlinedIcon from "@mui/icons-material/SourceOutlined";
import logo from "../../images/logo.png";
import logo_s from "../../images/logo_collapsed.png";
import "../../styles/Sidebar.css";

const Sidebar = () => {
  const [isSideBarCollapsed, setIsSideBarCollapsed] = useState(
    JSON.parse(localStorage.getItem("isSideBarCollapsed")) || false
  );
  const [openSubMenu, setOpenSubMenu] = useState("");
  const [openNestedSubMenu, setOpenNestedSubMenu] = useState("");
  const [openThirdLevelSubMenu, setOpenThirdLevelSubMenu] = useState("");
  const [activeItem, setActiveItem] = useState("");
  // Set when handleClick itself navigates, so the route-sync effect below
  // doesn't override the open/closed state the click just resolved (e.g.
  // re-opening Clients right after a click on it closed "Document Chasing").
  const navigatedFromSidebarRef = useRef(false);

  const { permissions, getPermissions } = useRoleContext();
  const { token, is_admin, dispatch } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    getPermissions?.();
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "isSideBarCollapsed",
      JSON.stringify(isSideBarCollapsed)
    );
  }, [isSideBarCollapsed]);

  const toggleSidebar = () => setIsSideBarCollapsed(!isSideBarCollapsed);

  const handleLogout = async () => {
    await logoutUser({ token });
    dispatch({ type: "LOGOUT" });
    navigate("/login");
  };

  const hasPermission = (permission) =>
    is_admin === "1" || permissions?.includes(permission);

  const handleClick = (item, level = "main") => {
    setActiveItem(item.name);

    if (level === "main") {
      // Always resolve openSubMenu here (not just when item.submenu is set)
      // so clicking a plain item like "Clients" closes whatever section
      // ("Data Center" > "All Documents", etc.) was previously left open.
      setOpenSubMenu((prev) =>
        item.submenu ? (prev === item.name ? "" : item.name) : ""
      );
      setOpenNestedSubMenu("");
      setOpenThirdLevelSubMenu("");
    } else if (item.submenu) {
      if (level === "nested") {
        setOpenNestedSubMenu((prev) => (prev === item.name ? "" : item.name));
        setOpenThirdLevelSubMenu("");
      } else if (level === "third") {
        setOpenThirdLevelSubMenu((prev) =>
          prev === item.name ? "" : item.name
        );
      }
    }

    if (item.path) {
      // Same-path clicks don't change location, so the effect won't run and
      // must not be left a stale skip flag.
      if (item.path !== location.pathname) {
        navigatedFromSidebarRef.current = true;
      }
      navigate(item.path);
    }

    if (item.action) {
      item.action();
    }
  };

  const baseRoutes = [
    {
      path: "/dashboard",
      name: "Dashboard",
      icon: <DashboardOutlinedIcon />,
    },
    {
      path: "/clients",
      name: "Clients",
      icon: <BusinessOutlinedIcon />,
      permission: "view_clients",
      submenu: [
        {
          path: "/document-chasing",
          name: "Document Chasing",
          icon: <MarkEmailUnreadOutlinedIcon />,
          permission: "view_clients",
        },
      ],
    },
    {
      // Main-level item right after Clients (same page, route and permission).
      path: "/documents",
      name: "All Documents",
      icon: <ArticleOutlinedIcon />,
      permission: "view_documents",
    },
    {
      // Main-level item right after All Documents (same page, route and
      // permission as before - moved out of Data Center).
      path: "/starred-documents",
      name: "Favourites",
      icon: <StarBorderOutlinedIcon />,
      permission: "view_all_documents",
    },
    {
      // Displayed name only - formerly "Mail Room"; routes, pages and
      // permissions are unchanged.
      name: "Data Center",
      icon: <MarkunreadMailboxOutlinedIcon />,
      submenu: [
        {
          path: "/unprocessed-documents",
          name: "Unprocessed Documents",
          icon: <PendingActionsOutlinedIcon />,
          permission: "view_unprocessed_documents",
          submenu: [
            {
              path: "/assigned-case-list",
              name: "All Assigned",
              icon: <AssignmentTurnedInOutlinedIcon />,
              permission: "view_all_assigned",
            },
            {
              path: "/unassigned-case-list",
              name: "All Unassigned",
              icon: <InboxOutlinedIcon />,
              permission: "view_all_unassigned",
            },
            {
              path: "/my-assigned-case-list",
              name: "My Assigned",
              icon: <AssignmentIndOutlinedIcon />,
              permission: "view_my_assigned",
            },
          ],
        },
        {
          path: "/processed-documents",
          name: "Processed Documents",
          icon: <DoneAllOutlinedIcon />,
          permission: "view_processed_documents",
        },
        {
          path: "/client-unknown-documents",
          name: "Client Unknown Documents",
          icon: <HelpOutlineOutlinedIcon />,
          permission: "view_client_unknown_documents",
        },
        {
          path: "/requests",
          name: "Document Requests",
          icon: <RequestPageOutlinedIcon />,
          permission: "view_requests",
        },
      ],
    },
    {
      // Main menu for the incoming-document sources (moved out of Data
      // Center). Menu labels only - routes, pages and permissions unchanged.
      name: "Data Source",
      icon: <SourceOutlinedIcon />,
      submenu: [
        {
          path: "/email-case-list",
          name: "Email",
          icon: <MailOutlineIcon />,
          permission: "view_incoming_emails",
        },
        {
          path: "/scan-case-list",
          name: "Scanner",
          icon: <DocumentScannerOutlinedIcon />,
          permission: "view_scanned_files",
        },
        {
          path: "/fax-case-list",
          name: "Fax",
          icon: <FaxOutlinedIcon />,
          permission: "view_incoming_faxes",
        },
      ],
    },
    {
      path: "/audit-trail",
      name: "Audit Trail",
      icon: <ManageHistoryOutlinedIcon />,
      permission: "view_documents",
      // Admin users only (like API Reference / Embed & API).
      adminOnly: true,
    },
    {
      name: "Administration",
      icon: <AdminPanelSettingsOutlinedIcon />,
      submenu: [
        {
          path: "/users",
          name: "Users",
          icon: <GroupOutlinedIcon />,
          permission: "view_users",
        },
        {
          path: "/roles",
          name: "Roles",
          icon: <SecurityOutlinedIcon />,
          permission: "view_roles",
        },
        {
          path: "/field-settings",
          name: "Field Settings",
          icon: <TuneOutlinedIcon />,
          permission: "view_field_settings",
        },
        {
          path: "/document-request-settings",
          name: "Document Requests Templates",
          icon: <RequestPageOutlinedIcon />,
          permission: "view_document_requests",
        },
        {
          path: "/email-templates",
          name: "Email Templates",
          icon: <DraftsOutlinedIcon />,
          permission: "view_email_templates",
        },
        {
          path: "/api-reference",
          name: "API Reference",
          icon: <ApiOutlinedIcon />,
          permission: "view_api_reference",
          adminOnly: true,
        },
        {
          path: "/embed-api",
          name: "Embed & API",
          icon: <IntegrationInstructionsOutlinedIcon />,
          adminOnly: true,
        },
      ],
    },
    {
      name: "Recycle Bin",
      icon: <DeleteOutlineIcon />,
      submenu: [
        {
          path: "/trash-users",
          name: "Users",
          icon: <GroupOutlinedIcon />,
          permission: "view_trash_users",
        },
        {
          path: "/trash-documents",
          name: "Documents",
          icon: <DescriptionOutlinedIcon />,
          permission: "view_trash_documents",
        },
      ],
    },
    {
      name: "Logout",
      icon: <LogoutOutlinedIcon />,
      path: "/login",
      action: handleLogout,
    },
  ];

  // Finds the item matching the current route and the chain of submenu-bearing
  // ancestors that must stay open to reveal it (the matched item itself is only
  // included when it has its own submenu, since leaf items don't own an "open"
  // slot). Used to keep the sidebar's expand state in sync with navigation that
  // doesn't go through handleClick — direct URLs, browser back/forward, or
  // links elsewhere in the app — so e.g. landing on Clients always collapses
  // Data Center instead of leaving it stuck open from an earlier click.
  const findMenuTrail = (items, pathname, trail = []) => {
    for (const item of items) {
      if (item.path === pathname) {
        return {
          activeName: item.name,
          trail: item.submenu ? [...trail, item.name] : trail,
        };
      }
      if (item.submenu) {
        const found = findMenuTrail(item.submenu, pathname, [
          ...trail,
          item.name,
        ]);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    if (navigatedFromSidebarRef.current) {
      navigatedFromSidebarRef.current = false;
      return;
    }
    const match = findMenuTrail(baseRoutes, location.pathname);
    if (!match) return;
    setActiveItem(match.activeName);
    setOpenSubMenu(match.trail[0] || "");
    setOpenNestedSubMenu(match.trail[1] || "");
    setOpenThirdLevelSubMenu(match.trail[2] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // adminOnly items (e.g. API Reference) are shown to admin users only,
  // regardless of role permissions; everything else uses its permission.
  const canSee = (item) =>
    (!item.adminOnly || is_admin === "1") &&
    (!item.permission || hasPermission(item.permission));

  const renderMenu = (items, level = "main") =>
    items.map((item, index) => {
      if (!canSee(item)) return null;

      const openState =
        level === "main"
          ? openSubMenu
          : level === "nested"
          ? openNestedSubMenu
          : openThirdLevelSubMenu;

      // Handle submenu: check if it has visible items
      let filteredSubmenu = item.submenu;
      if (item.submenu) {
        filteredSubmenu = item.submenu.filter(canSee);

        // If the submenu is empty and we're only showing submenus, skip rendering this parent
        if (filteredSubmenu.length === 0) return null;
      }

      const isActive = activeItem === item.name;
      const isOpen = !!filteredSubmenu && openState === item.name;
      // Presentation only: chevron on top-level items and submenu parents
      // (down when closed, up while that submenu is open), and a divider line before
      // items flagged dividerBefore. Behaviour is unchanged.
      // Arrow only on items that have a submenu (not on standalone items).
      const showChevron = !isSideBarCollapsed && !!filteredSubmenu;

      return (
        <React.Fragment key={index}>
          {item.dividerBefore && !isSideBarCollapsed && (
            <div className="sidebar-divider" role="separator" />
          )}
          <div
            onClick={() => handleClick(item, level)}
            className={`sidebar-list sidebar-level-${level} ${isActive ? "active-sidebar-list" : ""} ${isOpen ? "open-sidebar-list" : ""}`}
            style={item.name === "Logout" ? { marginTop: "40px" } : {}}
          >
            <Tooltip title={item.name} placement="right" arrow>
              <div className="sidebar-list-icon">{item.icon}</div>
            </Tooltip>
            {!isSideBarCollapsed && (
              <div className="sidebar-list-title">{item.name}</div>
            )}
            {!isSideBarCollapsed && item.badge != null && (
              <span className="sidebar-badge">{item.badge}</span>
            )}
            {showChevron && (
              <ExpandMoreIcon
                className={`sidebar-chevron ${isOpen ? "sidebar-chevron-open" : ""}`}
              />
            )}
          </div>

          {filteredSubmenu && openState === item.name && (
            <div className="submenu-list">
              {renderMenu(
                filteredSubmenu,
                level === "main" ? "nested" : "third"
              )}
            </div>
          )}
        </React.Fragment>
      );
    });

  return (
    <div className="sidebar-component">
      <div
        className={
          isSideBarCollapsed
            ? "collapsed-sidebar-container"
            : "sidebar-container"
        }
      >
        <div className="top-section">
          <NavLink to="/dashboard">
            <img src={isSideBarCollapsed ? logo_s : logo} alt="logo" />
          </NavLink>
          <Tooltip
            title={isSideBarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            placement="right"
            arrow
          >
            <div onClick={toggleSidebar} className="scrollable-bar">
              {isSideBarCollapsed ? (
                <KeyboardDoubleArrowRightOutlinedIcon />
              ) : (
                <KeyboardDoubleArrowLeftOutlinedIcon />
              )}
            </div>
          </Tooltip>
        </div>

        <section className="sidebar-list-section">
          {renderMenu(baseRoutes.filter((item) => item.name !== "Logout"))}
        </section>

        {/* Sticky Logout */}
        <div className="sidebar-logout">
          {renderMenu(baseRoutes.filter((item) => item.name === "Logout"))}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
